/**
 * Xendrel brand geometry, verbatim from the brand lockup file (10042 × 2038):
 * the mark on the left, the XENDREL wordmark to its right, both centred on the
 * same horizontal axis. Do not redraw or round the paths.
 *
 * Files that cannot import this module carry their own copy, held in step by
 * `xendrel-geometry.test.ts`: the static `public/xendrel-{icon,mark,logo}.svg`
 * (the favicon among them) and, in docs-site, `XendrelMark.tsx` and
 * `public/xendrel-logo.svg`.
 */

/** The whole lockup, exactly as delivered. */
export const LOGO_VIEW_BOX = '0 0 10042 2038'

/**
 * A square around the mark alone (1807 × 2034, centred on 903.5 / 1016.8) with
 * the same air a lucide icon keeps, so the mark matches an icon at one `size-*`.
 */
export const MARK_VIEW_BOX = '-276.5 -163.2 2360 2360'

/** Four chevrons closing in on a centre: top, bottom, right, left. */
export const MARK_PATHS = [
  'M1693.35,101.146l-97.987,-101.146l-689.879,668.317l-539.008,-507.379l-96.546,102.55l587.967,553.471l97.258,-0.696l738.179,-715.104l0.017,-0.017l-0,0.004Z',
  'M1466.321,1904.408l97.638,-101.492l-608.958,-585.888l-97.45,-0.192l-748.75,714.946l97.275,101.858l699.929,-668.333l560.329,539.096l-0.017,0l0.004,0.004Z',
  'M1709.808,1629.704l97.237,-101.892l-532.967,-508.575l341.858,-318.212l-95.971,-103.092l-396.562,369.117l-0.625,102.5l587.013,560.154l0.017,0Z',
  'M191.458,1345.625l97.308,101.825l395.488,-377.933l-0.05,-101.875l-587.013,-559.583l-97.187,101.946l533.613,508.663l-342.133,326.962l-0.017,0l-0.008,-0.004Z',
] as const

/** The wordmark, one path per letter: X, E, N, D, R, E, L. */
export const WORDMARK_PATHS = [
  'M2557.45,1582.763l395.537,-583.283l-372.967,-548.558l15.383,-33.037l126.946,0l312.604,468.525l312.588,-468.525l126.946,0l15.4,33.038l-372.971,548.558l395.537,583.283l-15.904,33.037l-126.458,0l-335.158,-504.008l-335.175,504.013l-126.442,0l-15.904,-33.037l0.033,0l0.004,-0.004Z',
  'M3720.762,1615.821l0,-1197.921l781.579,-0l0,132.675l-635.504,0l0,370.713l583.421,0l0,132.675l-583.421,0l0,429.167l635.504,0l0,132.675l-781.579,0l0,0.017Z',
  'M4745.625,1615.817l0,-1197.917l161.737,0l629.062,971.754l0,-971.754l146.113,0l0,1197.917l-161.233,-0l-629.583,-971.788l0,971.788l-146.096,0Z',
  'M5995.45,1615.817l0,-1197.917l468.958,0c288.767,0 433.158,144.808 433.158,434.408l0,329.096c0,289.6 -144.392,434.408 -433.158,434.408l-468.958,0l0,0.004Zm146.075,-132.675l331.563,0c185.608,0 278.404,-92.967 278.404,-278.888l0,-374.792c0,-185.921 -92.796,-278.888 -278.404,-278.888l-331.563,0l0,932.571l0,-0.004Z',
  'M7175.8,1615.817l0,-1197.917l566.754,0c177.275,0 265.921,88.646 265.921,265.921l0,191.492c0,150.279 -64.288,235.85 -192.863,256.738l227.588,448.992l-18.613,34.775l-122.988,0l-241.267,-475.033l-338.454,0l0,475.033l-146.079,0Zm146.075,-607.725l423.612,0c77.95,0 116.908,-39.375 116.908,-118.142l0,-222.5c0,-77.933 -38.975,-116.892 -116.908,-116.892l-423.612,0l0,457.533Z',
  'M8286.683,1615.821l-0,-1197.921l781.579,-0l0,132.675l-635.504,0l0,370.713l583.421,0l0,132.675l-583.421,0l0,429.167l635.504,0l0,132.675l-781.579,0l0,0.017Z',
  'M9311.546,1615.817l0,-1197.917l146.075,0l0,1065.242l583.421,-0l0,132.675l-729.496,0Z',
] as const

/**
 * Derived by hand, not part of the brand file: the centre lines of the top and
 * bottom chevrons, walked arm, tip, other arm and back. Recompute them if
 * `MARK_PATHS` ever changes — nothing checks them.
 */
export const MARK_TOP_TRACK = 'M1644.4,50.6L906,742.5L318.2,212.2L906,742.5Z'
export const MARK_BOTTOM_TRACK = 'M157.4,1982.7L906.1,1291.1L1515.1,1853.7L906.1,1291.1Z'
